import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  inject,
  Injectable,
  OnDestroy,
  OnInit,
  Pipe,
  PipeTransform,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MatNativeDateModule,
  NativeDateAdapter,
} from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import {
  DateRange,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WeekSelectionStrategy } from './week-selection-strategy';
import {
  format,
  isToday,
  isValid,
  parseISO,
  startOfWeek,
} from 'date-fns';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { provideNgxMask } from 'ngx-mask';
import { animate, style, transition, trigger } from '@angular/animations';
import { TimerComponent, TimerDialogResult } from '../timer/timer.component';
import { MatDialog } from '@angular/material/dialog';
import { Project, TypesUI } from '../models/project';
import { NotesComponent } from '../notes/notes.component';
import { ProjectsService } from '../services/projects.service';
import { TimeDateUtils } from './date-utils';
import { TimesheetService } from '../services/time-sheet.service';
import { isOfficialLeaveCell, needsOfficialLeave } from '../services/official-leave.service';
import { TimeEntry, TimesheetStatus } from '../models/time-entry';
import { NotificationComponent } from '../components/notification/notification.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TimesheetClippy } from './timesheet-clippy';
import { ConfirmComponent } from '../dialogs/confirm/confirm.component';
import { MatCardModule } from '@angular/material/card';
import { CommunityAuthService } from '../services/community-auth.service';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { User } from '../models/user';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '../../environments/environment';
import { EffortUI } from '../models/effort';
import { UsersService } from '../services/user.service';
import { UiPreferencesService } from '../services/ui-preferences.service';
import {
  AppVersion,
  ProjectTimeTypeShort,
  TimesheetUIPreferences,
  UIPreference,
  WeekDayAbbr,
} from '../models/ui-preferences';
import { ContextMenuModel } from '../models/context-menu-model';
import { CellContextMenuComponent } from '../components/cell-context-menu/cell-context-menu.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { ThemeService } from '../../assets/theme/_theme.service';
import { MatChipsModule } from '@angular/material/chips';
import {
  MauiHolidaysService,
  MauiOfficeClosedHoliday,
} from '../services/maui-holidays.service';
import { TimesheetShadowProjectsService } from './timesheet-shadow-projects.service';
import { TimesheetKeyboardNavigationService } from './timesheet-keyboard-navigation.service';
import { TimesheetClipboardService } from './timesheet-clipboard.service';
import { TimesheetProjectViewService } from './timesheet-project-view.service';
import { TimesheetWeekNavigationService } from './timesheet-week-navigation.service';
import {
  applyHoursFormatting,
  buildTimesheetColumns,
  effortDayTotal,
  getWeekDays,
  projectTypeDayTotal,
  rowGroupTotal,
  rowProjectTotal,
  rowTotal,
  timesheetTotal,
  totalHoursFloatForDay,
  totalHoursForDay,
  weekEnd,
} from './time-tracker.utils';

export const MY_FORMATS = {
  parse: {
    dateInput: 'yyyy-MM-dd',
  },
  display: {
    dateInput: 'yyyy-MM-dd',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'yyyy-MM-dd',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: Object): string {
    return format(date, 'MMM dd, yyyy'); // Customize your format here
  }
}

@Pipe({
  name: 'hours',
})
export class HoursFormatter implements PipeTransform {
  private sanitizer = inject(DomSanitizer);
  transform(hours: number | string): SafeHtml {
    const num = Number(typeof hours === 'string' ? parseFloat(hours) : hours);
    if (num === 0) return this.sanitizer.bypassSecurityTrustHtml('&dash;');
    return num.toFixed(1);
  }
}

@Component({
  selector: 'app-time-tracker',
  templateUrl: './time-tracker.component.html',
  styleUrls: ['./time-tracker.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatTableModule,
    MatNativeDateModule,
    MatIconModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatCardModule,
    RouterModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    HoursFormatter,
    CellContextMenuComponent,
    MatCheckboxModule,
    MatListModule,
    MatSlideToggleModule
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: WeekSelectionStrategy,
    },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    provideNgxMask(),
    TimesheetKeyboardNavigationService,
    TimesheetClipboardService,
    TimesheetProjectViewService,
    TimesheetWeekNavigationService,
  ],
  animations: [
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: '0' }),
        animate('0.2s ease-in', style({ opacity: '1' })),
      ]),
      transition(':leave', [
        style({ opacity: '1' }),
        animate('0.2s ease-out', style({ opacity: '0' })),
      ]),
    ]),
  ],
})
export class TimeTrackerComponent implements OnInit, OnDestroy {
  isOfficialLeaveCell = isOfficialLeaveCell;
  officialLeaveUnavailable = false;
  readonly officialLeaveHelp = 'Imported from approved official leave records. Complete leave in the official leave application.';
  AppVersion = AppVersion;
  @ViewChild(MatTable, { static: false }) table!: MatTable<Project>;
  today = new Date();
  selectedWeek: DateRange<Date> = new DateRange<Date>(
    startOfWeek(this.today),
    weekEnd(startOfWeek(this.today)),
  );
  weekDays: string[] = [];
  displayedColumns: string[] = ['project'];
  displayedGroupHeaderRow: string[] = [];
  displayedPinnedHeaderRow: string[] = [];
  displayedProjWithTypesRow: string[] = [];
  projects: Project[] = [];
  shadowProjects: Project[] = []; // For detecting changes.
  isSubmitted = false;
  isReadonly = false;
  isApproved = false;
  isForReview = false;
  reviewerComments?: string = '';
  private loadingSubject = new BehaviorSubject<boolean>(true);
  isLoading = this.loadingSubject.pipe(debounceTime(environment.spinnerDelay + 0));
  effortUI: { [id: number]: EffortUI } = {};
  typesUI: Map<number, TypesUI> = new Map<number, TypesUI>();
  displayedHolidayColumns: string[] = ['header-holidays-label'];
  displayedTotalsColumns: string[] = ['header-subtotals-label'];
  isDarkMode = false;
  private holidaySubscription?: { unsubscribe(): void };
  private loadedHolidayWeekStart?: string;

  private scrollOpts = inject(ScrollStrategyOptions);
  private destroyRef = inject(DestroyRef);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private projectService = inject(ProjectsService);
  private timesheetService = inject(TimesheetService);
  private sanitizer = inject(DomSanitizer);
  private authService = inject(CommunityAuthService);
  private route = inject(ActivatedRoute);
  private userService = inject(UsersService);
  private uiPreferencesService = inject(UiPreferencesService);
  private router = inject(Router);
  private mauiHolidaysService = inject(MauiHolidaysService);
  private shadowProjectsService = inject(TimesheetShadowProjectsService);
  private keyboardNavigation = inject(TimesheetKeyboardNavigationService);
  private projectView = inject(TimesheetProjectViewService);
  private weekNavigation = inject(TimesheetWeekNavigationService);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private snackbar = inject(MatSnackBar);
  private themeService = inject(ThemeService);
  private currentWeekStart = startOfWeek(this.today);
  public rangeForm: FormGroup<{ start: FormControl<Date | null>, end: FormControl<Date | null> }>
     = this.fb.group({
      start: new FormControl(this.currentWeekStart),
      end: new FormControl(weekEnd(this.currentWeekStart)),
    });
  public fmlaDismissed = false;
  public clipboard = inject(TimesheetClipboardService);
  currentUser?: User;
  isPrivilegedUser: boolean = false;
  tempChanges?: Project[];
  projects$?: Observable<{ total: number; projects: Project[] }>;
  uiPrefChangedSubject = new BehaviorSubject<UIPreference>(new UIPreference());
  uiPrefChanged$ = this.uiPrefChangedSubject.asObservable();
  uiPreference?: UIPreference;
  tsStatus?: TimesheetStatus;
  currentlyLoadedWeek? = '';
  timesheetBacklog: [string, string][] = [];
  get sessionHideHelper() {
    const h = sessionStorage['hideHelper'];
    return h !== undefined
      ? h === 'true'
        ? AppVersion < 2
        : parseInt(h) >= AppVersion
      : undefined;
  }
  set sessionHideHelper(value: boolean | number | undefined) {
    sessionStorage['hideHelper'] = value;
  }
  get prefHideHelper() {
    const h = this.uiPreference?.timesheet?.hideHelper;
    if (h === undefined) return undefined;
    if (typeof h === 'boolean') return AppVersion < 2 ? h : false;
    return h >= AppVersion;
  }
  arePreferencesLoaded: boolean = false;
  officeClosedHolidays: Record<string, MauiOfficeClosedHoliday> = {};
  get hasOfficeClosedHolidays(): boolean {
    return Object.keys(this.officeClosedHolidays).length > 0;
  }

  public ngOnInit() {
    this.timesheetService.loadError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadingSubject.next(false);
        this.isReadonly = true;
        this.projects = [];
        this.shadowProjects = [];
        this.showError('Unable to load timesheet', 'Timesheet or official leave data could not be loaded. Please reload to try again.', 'Close', '');
      });
    this.clipboard.configure({
      getWeekStart: () => this.selectedWeek.start,
      getProjects: () => this.projects,
      afterChange: () => this.checkForAutoSave(),
    });
    this.themeService.isDarkMode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dark) => {
        this.isDarkMode = dark;
      });
  }

  dayOfWeek(day: string) {
    return new Date(day).getDay();
  }

  isToday(day: string) {
    return isToday(parseISO(day));
  }

  ngAfterViewInit() {
    const p = this.route.snapshot.queryParams;
    this.authService.isImpersonated
      .pipe(
        switchMap((_) => {
          this.loadingSubject.next(true);
          return this.userService.loadCurrentUser();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((user) => {
        this.currentUser = user;
        this.timesheetBacklog = user.mostRecentTSStatuses ?? [];
        const uiPreference = this.uiPreferencesService.fromUser(user);
        this.projects = [];
        this.uiPrefChangedSubject.next(uiPreference);
        this.isPrivilegedUser = user.userRoles.some((ur) =>
          ['admin', 'fullaccess'].includes(
            ur?.role?.roleName?.toLowerCase(),
          ),
        );
        this.chooseStartingWeek(uiPreference, p);
      });
    this.rangeForm.valueChanges
      .pipe(
        debounceTime(50),
        filter((e) => e.start !== undefined && e.start !== null && e.end !== undefined && e.end !== null),
        map((e) => ({ start: e.start!, end: e.end! })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(this.onRangeChange.bind(this));
    this.projects$ = this.authService.isProfileLoaded$.pipe(
      filter((isLoaded) => isLoaded),
      distinctUntilChanged(),
      switchMap((isLoaded) => {
        const dir = 'asc';
        return this.projectService.filterProjects(
          [
            {
              chip: 'member.hawkid',
              value: this.authService.applicationUsername ?? '',
            },
          ],
          10000,
          0,
          [
            ['isHR', dir],
            ['effortTitle', dir],
            ['title', dir],
          ],
        );
      }),
    );
    this.projects$ = this.projects$.pipe(shareReplay({ bufferSize: 1, refCount: true }));
    combineLatest([
      this.projects$,
      this.timesheetService.timeSheet$,
      this.uiPrefChanged$,
    ])
      .pipe(
        tap(([p, ts, _]) => {
          if (ts.status !== this.tsStatus) this.projects = [];
        }),
        debounceTime(50),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([projRes, ts, uiPreference]) => {
        this.officialLeaveUnavailable = ts.officialLeaveUnavailable ?? false;
        this.uiPreference = uiPreference;
        this.arePreferencesLoaded = true;
        const daysWithHours = this.projectView.daysWithHours(ts);
        this.updateWeek(daysWithHours);
        this.loadingSubject.next(false);
        const hasChanges = this.tempChanges || this.hasWeekDataChanged();
        const preProjects = this.tempChanges ?? this.projects;
        this.tempChanges = undefined;
        const projectView = this.projectView.buildView({
          projects: projRes.projects,
          timesheet: ts,
          uiPreference,
          weekDays: this.weekDays,
          previousProjects: preProjects,
          typesUI: this.typesUI,
          currentWeekStart: this.currentlyLoadedWeek,
          preservePreviousChanges: !!hasChanges,
        });
        this.currentlyLoadedWeek = ts.weekStart;
        this.tsStatus = projectView.status;
        this.reviewerComments = projectView.reviewerComments;
        this.isReadonly = projectView.isReadonly;
        this.isSubmitted = projectView.isSubmitted;
        this.isApproved = projectView.isApproved;
        this.isForReview = projectView.isForReview;
        this.projects = projectView.projects;
        this.shadowProjects = projectView.shadowProjects;
        this.effortUI = projectView.effortUI;
        this.typesUI = projectView.typesUI;

        this.table.renderRows();
        this.changeDetectorRef.detectChanges();
      });
  }

  chooseStartingWeek(uiPreference: UIPreference, p: Params) {
    const range = this.weekNavigation.chooseStartingWeek({
      queryWeek: p['weekof'],
      preference: uiPreference.timesheet,
      recentStatuses: this.currentUser?.mostRecentTSStatuses,
    });
    this.changeWeek(range.start, range.end);
  }

  dismissFmlaWarning() {
    this.fmlaDismissed = true;
    return false;
  }

  setPrefDisplayDays(weekdays: string[], show: boolean) {
    this.saveUI({ daysToHide: show ? [] : <WeekDayAbbr[]>weekdays });
    this.updateWeek();
  }

  readonly timesheetStatusSummaryVerbs: { [key: string]: string } = {
    approved: 'has been approved',
    submitted: 'has been submitted',
    'not created': 'has not been created',
    review: 'returned for review',
    unsubmitted: 'is in progress',
  };
  getTimesheetStatusSummary(weekStart: string, status: string) {
    const lstatus = status.toLowerCase();
    const sverb = this.timesheetStatusSummaryVerbs[lstatus] || 'is ' + lstatus;
    return (
      'Timesheet ' + sverb + ' for week of ' + format(weekStart, 'MMM do') + '.'
    );
  }

  isThisWeek(weekStart: string): boolean {
    // Backlog dates may include a time component, and fromWeekString falls
    // back to today for anything it can't parse, so parse and check directly.
    const start = parseISO(weekStart);
    return isValid(start) && this.weekNavigation.isSameWeek(start, new Date());
  }

  ngOnDestroy() {
    this.holidaySubscription?.unsubscribe();
  }

  setWeek(weekof: string): void {
    const range = this.weekNavigation.fromWeekString(weekof);
    this.changeWeek(range.start, range.end);
  }

  updateWeek(daysWithHours: string[] = []): void {
    this.loadingSubject.next(true);
    const selectedStart = this.selectedWeek.start ?? new Date();
    this.weekDays = getWeekDays(
      selectedStart,
      this.uiPreference?.timesheet?.daysToHide,
      daysWithHours,
    );
    this.loadOfficeClosedHolidays(selectedStart);
    const columns = buildTimesheetColumns(this.weekDays);
    this.displayedColumns = columns.displayedColumns;
    this.displayedPinnedHeaderRow = columns.displayedPinnedHeaderRow;
    this.displayedProjWithTypesRow = columns.displayedProjWithTypesRow;
    this.displayedGroupHeaderRow = columns.displayedGroupHeaderRow;
    this.displayedHolidayColumns = columns.displayedHolidayColumns;
    this.displayedTotalsColumns = columns.displayedTotalsColumns;
  }

  loadOfficeClosedHolidays(weekStart: Date): void {
    const weekStartKey = format(startOfWeek(weekStart), 'yyyy-MM-dd');
    if (weekStartKey === this.loadedHolidayWeekStart) return;

    this.loadedHolidayWeekStart = weekStartKey;
    this.officeClosedHolidays = {};
    this.holidaySubscription?.unsubscribe();
    this.holidaySubscription = this.mauiHolidaysService
      .getOfficeClosedHolidaysForWeek(weekStart)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((holidays) => {
        this.officeClosedHolidays = holidays;
      });
  }

  officeClosedHoliday(day: string): MauiOfficeClosedHoliday | undefined {
    return this.officeClosedHolidays[day];
  }

  projectTrackBy(index: number, proj: Project) {
    return proj.ui?.virtualId;
  }

  nextPrevClick(offset: number): void {
    const range = this.weekNavigation.offsetWeek(this.selectedWeek.start, offset);
    this.changeWeek(range.start, range.end);
  }

  changeWeek(start: Date, end: Date) {
    const range = this.weekNavigation.normalizeRange(start, end);
    const applyChange = () => this.applyWeekChange(range.start, range.end);

    if (!this.hasWeekDataChanged()) {
      applyChange();
      return;
    }

    this.confirmAbandonChanges(applyChange);
  }

  private applyWeekChange(start: Date, end: Date): void {
    this.rangeForm.get('start')?.setValue(start, { emitEvent: false });
    this.rangeForm.get('end')?.setValue(end, { emitEvent: false });
    this.selectedWeek = new DateRange(start, end);
    console.log('Changing week to:', this.selectedWeek);
    this.updateWeek();
    this.saveUI({ lastWeekViewed: '' + start }, false);
    this.router.navigate(['/timesheet'], {
      queryParams: { weekof: format(start, 'yyyy-MM-dd') },
      replaceUrl: true,
      info: { skipEvents: false },
    });
    const weekStart = TimeDateUtils.getDay(start);
    if (needsOfficialLeave(weekStart)) {
      this.timesheetService.fetchTimesheet(weekStart, this.projects$?.pipe(map(result => result.projects)));
    } else {
      this.timesheetService.fetchTimesheet(weekStart);
    }
  }

  private confirmAbandonChanges(onConfirm: () => void): void {
    const message =
      'There are unsaved changes to the current week. Would you like to proceed?';
    this.dialog
      .open(ConfirmComponent, {
        data: { title: 'Abandon changes?', message },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) onConfirm();
      });
  }

  hasWeekDataChanged(): boolean {
    return this.shadowProjectsService.hasChanges(
      this.projects,
      this.shadowProjects,
      this.weekDays,
    );
  }

  thisWeekClick(): void {
    const range = this.weekNavigation.weekContaining(new Date());
    this.changeWeek(range.start, range.end);
  }

  hasExcessHours() {
    return this.weekDays.some((day) => this.getTotalHoursFloat(day) > 24);
  }

  onRangeChange(ev: { start: Date; end: Date }) {
    if (this.weekNavigation.isSameWeek(ev.start, this.selectedWeek.start)) return;
    this.changeWeek(ev.start, ev.end);
  }

  startTimer(project: Project, day: string) {
    if (this.isReadonly || isOfficialLeaveCell(project, day)) return;
    const existingHours = parseFloat(project.hours[day]) * 3600;
    const startHours = isNaN(existingHours) ? 0 : existingHours;
    const dialogRef = this.dialog.open(TimerComponent, {
      data: {
        startTime: startHours,
        project,
        day,
        weekStart: this.weekDays[0],
      },
      hasBackdrop: false,
      scrollStrategy: this.scrollOpts.reposition(),
      disableClose: true,
      closeOnNavigation: false,
      panelClass: 'custom-mat-dialog-panel',
    });
    dialogRef
      .afterClosed()
      .subscribe((result: TimerDialogResult | undefined) => {
        this.focusTimeInput(project, day);
        const proj = this.getFreshProject(project);
        if (result && proj) {
          proj.hours[day] = result.displayHours;
          proj.notes[day] = result.notes;
          proj.review[day] = { isPleaseReview: false };
          console.info(
            this.startTimer.name,
            'Elapsed Time:',
            result.elapsedTime,
            ' on ',
            day,
            proj.key,
          );
          console.info(this.startTimer.name, 'Notes:', result.notes);
        } else {
          console.info('Should clear hours.');
        }
      });
  }

  openNotesDialog(
    project: Project,
    day: string,
    readonly: boolean = false,
  ): void {
    readonly ||= this.isReadonly || isOfficialLeaveCell(project, day);
    const dialogRef = this.dialog.open(NotesComponent, {
      width: '400px',
      autoFocus: true,
      hasBackdrop: true,
      data: {
        notes: project.notes[day],
        readonly,
        projectId: project.id,
        timeTypeId: project.ui.timeTypeId,
        day,
        weekStart: this.getWeekStart(),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.focusTimeInput(project, day);
      const proj = this.getFreshProject(project);
      if (!readonly && result !== null && result !== undefined && proj) {
        proj.notes[day] = result;
      }
    });
  }

  private timeInputId(project: Project, day: string) {
    return `timeInput${project.id}-${day}-${project.ui.timeTypeId}`;
  }

  private focusTimeInput(project: Project, day: string): void {
    document.getElementById(this.timeInputId(project, day))?.focus();
  }

  getFreshProject(project: Project) {
    const projectId = project.id;
    const timeTypeId = project.ui.timeTypeId;
    const freshProj = this.projects.find(
      (p) => p.id === projectId && p.ui.timeTypeId === timeTypeId,
    );
    if (!freshProj) {
      this.snackbar.open(
        `Project "${project.title}" with time type "${timeTypeId}" no longer available.`,
      );
      return;
    }
    return freshProj;
  }

  submitWeek() {
    if (this.uiPreference?.timesheet?.confirmTSSubmit) {
      this.dialog
        .open(ConfirmComponent, {
          data: {
            title: 'Are you sure you want to submit?',
            message:
              "You won't be able to edit this timesheet again unless your supervisor returns it to you.",
            showDontAskAgain: true,
          },
        })
        .afterClosed()
        .subscribe((c: { confirm: boolean; dontAskAgain: boolean } | false) => {
          if (c) {
            this.saveUI({ confirmTSSubmit: !c.dontAskAgain });
            this.saveWeek(true);
          }
        });
    } else {
      this.saveWeek(true);
    }
  }

  saveWeek(submit = false) {
    console.info('Saving week');
    this.isApproved = false;
    this.isSubmitted = false;
    this.loadingSubject.next(true);
    const entries = this.buildTimeEntries();
    const weekStartF = this.getWeekStart();
    this.timesheetService
      .updateTimesheet(entries, weekStartF, submit, true)
      .pipe(tap((_) => this.loadingSubject.next(false)))
      .subscribe({
        next: () => this.handleSaveSuccess(submit),
        error: (error) => this.handleSaveError(error),
      });
  }

  private buildTimeEntries(): TimeEntry[] {
    const entries: TimeEntry[] = [];
    for (const day of this.weekDays) {
      for (const proj of this.projects) {
        if (isOfficialLeaveCell(proj, day)) continue;
        this.shadowProjectsService.syncCell(proj, this.shadowProjects, day);
        entries.push(this.buildTimeEntry(proj, day));
      }
    }
    return entries;
  }

  private buildTimeEntry(proj: Project, day: string): TimeEntry {
    const hours = parseFloat(proj.hours[day]);
    const entry = new TimeEntry();
    entry.notes = proj.notes[day];
    entry.recordedTime = isNaN(hours) ? undefined : hours;
    entry.projectId = proj.id;
    entry.dateEntry = day;
    entry.timeTypeId = proj.ui.timeTypeId;
    return entry;
  }

  private handleSaveSuccess(submit: boolean): void {
    this.userService.loadCurrentUser().subscribe((u) => {
      this.timesheetBacklog = u.mostRecentTSStatuses ?? [];
    });

    if (this.hasExcessHours()) {
      const message = this.sanitizer.bypassSecurityTrustHtml(
        'Your timesheet contains more than 24 hours in a single day. Your entries have been saved. Please correct this before submitting your time.',
      );
      this.showError('Error:', message, 'Close', '');
      return;
    }

    this.snackbar.open(`Timesheet ${submit ? 'submitted' : 'saved'}.`, '', {
      duration: 5_000,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: 'success-snackbar',
    });
  }

  private handleSaveError(error: { message?: string; error?: unknown }): void {
    const errorSummaryTrusted = this.sanitizer.bypassSecurityTrustHtml(
      this.buildSaveErrorSummary(error),
    );
    this.showError(
      'ERROR:',
      'An error occurred while saving your timesheet. Please try again, or record your hours separately to enter later.',
      '',
      errorSummaryTrusted,
    );
  }

  private buildSaveErrorSummary(error: {
    message?: string;
    error?: unknown;
  }): string {
    const errorMessage = error.message ?? 'Unknown error';
    const connectionId = error.error
      ?.toString()
      .match(/ClientConnectionId:([a-f0-9-]+)/)?.[1];
    const connectionDetails = connectionId
      ? `<b>Logs.Exception (ClientConnectionId):</b><br />${connectionId}`
      : '';

    return `If the error persists, please contact the <b>ITS Help Desk</b> with the following information:<br /><br /><b>HTTP Error message:</b><br />${errorMessage}<br /><br />${connectionDetails}`;
  }

  getWeekStart() {
    const weekStart = new Date(this.selectedWeek.start ?? new Date());
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const weekStartF = TimeDateUtils.getDay(weekStart);
    return weekStartF;
  }

  showError(
    title: string,
    description: string | SafeHtml,
    dismissLabel: string,
    details: string | SafeHtml,
  ) {
    return this.snackbar.openFromComponent(NotificationComponent, {
      data: {
        title,
        description,
        dismissLabel,
        details,
        icon: 'assets/images/warning.png',
      },
      verticalPosition: 'bottom',
      horizontalPosition: 'center',
      panelClass: ['custom-snackbar'],
    });
  }

  expandRow(project: Project) {
    for (const proj of this.projects) {
      if (proj === project) continue;
      proj.ui.expanded = false;
    }

    project.ui.expanded = !project.ui.expanded;
    const ids = Object.keys(this.projects)
      .map((k) => parseInt(k))
      .filter((k) => this.projects[k].ui.expanded);
    this.saveUI({ expandedProjectIds: ids }, false);
    this.changeDetectorRef.detectChanges();
  }

  isNextGroup = ((i: number) => {
    return this.projectView.isNextGroup(this.projects, i);
  }).bind(this);

  firstAndPinned = ((i: number) => {
    return this.projectView.firstAndPinned(this.projects, i);
  }).bind(this);

  anyPinned() {
    return this.projectView.anyPinned(this.projects);
  }

  isNextProjectWithTypes = ((i: number) => {
    return this.projectView.isNextProjectWithTypes(
      this.projects,
      i,
      !!this.isSubmitted,
      !!this.isForReview,
    );
  }).bind(this);

  projectHasHours = ((row: Project) => {
    return this.projectView.projectHasHours(row, this.weekDays);
  }).bind(this);

  handleNavigationKeydown(e: KeyboardEvent) {
    this.keyboardNavigation.handleNavigation(e);
  }

  handleHoursKeydown(e: KeyboardEvent, project: Project, day: string) {
    if (this.isReadonly || isOfficialLeaveCell(project, day)) return;
    this.keyboardNavigation.handleHoursKey(
      e, project, day,
      !!this.uiPreference?.timesheet?.allowTwoDigitEntry,
      {
        openNotes: (project, day) => this.openNotesDialog(project, day),
        startTimer: (project, day) => this.startTimer(project, day),
        togglePin: (project) => this.togglePinProject(project),
      },
    );
  }

  dismissGreeting(doneWithIt: boolean) {
    if (doneWithIt) {
      delete sessionStorage['hideHelper'];
      this.saveUI({ hideHelper: AppVersion }, false);
    } else {
      this.sessionHideHelper = AppVersion;
    }
  }

  public toggleEffort(project: Project) {
    const effortId = project.effort.id;
    const effortState = (this.effortUI[effortId] ??= {});
    effortState.isExpanded = !effortState.isExpanded;

    const ids = Object.keys(this.effortUI)
      .map((k) => parseInt(k))
      .filter((k) => this.effortUI[k].isExpanded);
    if (!this.isReadonly) this.saveUI({ expandedEffortIds: ids }, false);
  }

  public isExpanded(project: Project, isTypesHeader: boolean) {
    return this.projectView.isExpanded(
      this.projects,
      this.effortUI,
      this.typesUI,
      this.weekDays,
      project,
      isTypesHeader,
      !!this.isReadonly,
    );
  }

  public isGrouped(project: Project) {
    return this.projectView.isGrouped(this.projects, project);
  }

  public toggleProjectTypes(project: Project) {
    if (this.isReadonly) return;
    const projectId = project.id;
    if (projectId === undefined) {
      console.warn('Project ID is undefined. Cannot toggle project types.');
      return;
    }

    const uistate = this.typesUI.get(projectId) ?? {};
    uistate.isExpanded = !uistate.isExpanded;
    this.typesUI.set(projectId, uistate);

    const ids = Array.from(this.typesUI.keys()).filter(
      (k: number) => this.typesUI.get(k)?.isExpanded,
    );
    this.saveUI({ expandedProjectIds: ids }, false);
  }

  public isExpandedTypes(project: Project) {
    return this.projectView.isExpandedTypes(this.typesUI, project);
  }

  public showTimeType(project: Project) {
    return this.projectView.showTimeType(this.projects, project);
  }

  public togglePinProject(project: Project) {
    if (this.isReadonly) return;
    const projectId = project.id;
    const timeTypeId = project.ui.timeTypeId;
    if (projectId === undefined || timeTypeId === undefined) {
      console.warn('Project ID or Time Type ID is undefined. Cannot toggle pin.');
      return;
    }

    const currentPinnedProjectTimeTypes =
      this.uiPreference?.timesheet?.pinnedProjectTimeTypes ?? [];
    const existing = currentPinnedProjectTimeTypes.find(
      (p) => p.projectId === projectId && p.timeTypeId === timeTypeId,
    );
    const pinnedProjectTimeTypes = existing
      ? currentPinnedProjectTimeTypes.filter((p) => p !== existing)
      : [
          ...currentPinnedProjectTimeTypes,
          <ProjectTimeTypeShort>{ projectId, timeTypeId },
        ];

    this.saveUI({ pinnedProjectTimeTypes }, true);
  }

  rowTotal(proj: Project) {
    return rowTotal(proj, this.weekDays);
  }

  rowGroupTotal(proj: Project) {
    return rowGroupTotal(
      proj,
      this.projects,
      this.weekDays,
      this.firstAndPinned(this.projects.findIndex((p) => p === proj)),
    );
  }

  rowProjectTotal(proj: Project) {
    return rowProjectTotal(proj, this.projects, this.weekDays);
  }

  total() {
    return timesheetTotal(this.projects, this.weekDays);
  }

  getTotalHours(day: string) {
    return totalHoursForDay(this.projects, day);
  }

  getTotalHoursFloat(day: string) {
    return totalHoursFloatForDay(this.projects, day);
  }

  getProjectTypeDayTotal(project: Project, day: string) {
    return projectTypeDayTotal(project, this.projects, day);
  }

  getEffortDayTotal(project: Project, day: string) {
    return effortDayTotal(project, this.projects, day);
  }

  uiPrefSource = new Subject<boolean>();
  uiPref$ = this.uiPrefSource.asObservable();
  private readonly uiPrefEffect = this.uiPref$
    .pipe(debounceTime(314), takeUntilDestroyed(this.destroyRef))
    .subscribe((resetTable) => {
      if (!this.uiPreference || !this.currentUser) {
        console.warn('UI preference or current user is not set. Cannot save UI preferences.');
        return;
      }
      this.uiPreferencesService
        .save(this.currentUser.id, this.uiPreference)
        .subscribe((uipref) => {
          this.uiPrefChangedSubject.next(uipref);
          if (resetTable) {
            this.tempChanges = this.projects;
            this.projects = [];
          }
        });
    });

  /**
   * Consider whether to auto-save. Can depend on whether there are changes or (in the future) whether the user has auto-save enabled.
   * If a save is indicated, it is scheduled through a debouncer.
   */
  public checkForAutoSave() {
    this.applyHoursFormatting();
    const hasChanges = this.hasWeekDataChanged();
    console.log('Checking for autosave. Has changes: ', hasChanges);
    if (hasChanges) {
      this.autoSaveSubject.next(true);
    }
  }
  autoSaveSubject = new Subject();
  autoSave$ = this.autoSaveSubject.asObservable();
  private readonly autoSaveEffect = this.autoSave$
    .pipe(
      debounceTime(environment.tsAutoSaveDelay),
      takeUntilDestroyed(this.destroyRef),
    )
    .subscribe((_) => {
      // Still no manual save / submit? Then go ahead and autosave.
      if (this.hasWeekDataChanged()) this.saveWeek(false);
    });

  public applyHoursFormatting() {
    applyHoursFormatting(
      this.projects,
      this.weekDays,
      this.uiPreference?.timesheet,
    );
  }

  public saveUI(prefs: TimesheetUIPreferences, resetTable: boolean = true) {
    if (!this.uiPreference || !this.currentUser) return;
    this.uiPreference = this.uiPreferencesService.patchTimesheet(
      this.uiPreference,
      prefs,
    );
    this.uiPrefSource.next(resetTable);
  }

  isDisplayContextMenu = false;
  notesKey = 'notes';
  timerKey = 'timer';
  rightClickMenuItems: ContextMenuModel[] = [
    { menuEvent: this.notesKey, menuText: 'Enter Notes' },
    { menuEvent: this.timerKey, menuText: 'Start Timer' },
  ];
  rightClickMenuPositionX: number = 0;
  rightClickMenuPositionY: number = 0;
  contextMenuProject?: Project;
  contextMenuDay?: string;
  displayContextMenu(event: MouseEvent, project: Project, day: string) {
    if (this.isReadonly || isOfficialLeaveCell(project, day)) return false;
    this.contextMenuProject = project;
    this.contextMenuDay = day;
    this.rightClickMenuPositionX = event.clientX;
    this.rightClickMenuPositionY = event.clientY;
    this.isDisplayContextMenu = true;
    return false;
  }

  getRightClickMenuStyle() {
    return {
      position: 'fixed',
      left: `${this.rightClickMenuPositionX}px`,
      top: `${this.rightClickMenuPositionY}px`,
    };
  }

  handleMenuItemClick(event: { event: Event; data: string }) {
    if (!this.contextMenuProject || !this.contextMenuDay) {
      console.warn(
        'Context menu project or day is not set. Ignoring menu item click.',
      );
      return;
    }
    switch (event.data) {
      case this.notesKey:
        this.openNotesDialog(this.contextMenuProject, this.contextMenuDay);
        break;
      case this.timerKey:
        this.startTimer(this.contextMenuProject, this.contextMenuDay);
    }
  }

  @HostListener('document:click')
  closeContextMenu(): void {
    this.isDisplayContextMenu = false;
  }
}
